import {useLazyQuery} from '@apollo/client/react';
import {extractGqlData} from '@coolcolduk/ui-apollo-client';
import {useEffect, useState} from 'react';
import {SeedQuestionStatus} from '@cp/model';
import {COUNT_SEED_QUESTIONS_BY_FILTERS} from '../sharedQueries';

export interface CountSeedQuestionsVariables {
  moduleId: string;
  topics?: string[];
  statuses?: SeedQuestionStatus[];
}

export function useCountSeedQuestions() {
  const [countSeedQuestionsQuery, {loading, error, data}] = useLazyQuery<
    {countSeedQuestionsByFilters: number},
    CountSeedQuestionsVariables
  >(COUNT_SEED_QUESTIONS_BY_FILTERS);

  const countSeedQuestions = async (variables: CountSeedQuestionsVariables) => {
    const result = await countSeedQuestionsQuery({
      variables,
    });
    return extractGqlData(result).countSeedQuestionsByFilters || 0;
  };

  return {
    countSeedQuestions,
    loading,
    error,
    count: data?.countSeedQuestionsByFilters || 0,
  };
}

/**
 * Fetch count automatically when parameter updated
 * @param moduleId
 * @param topics
 * @param statuses
 * @returns undefined without module, number of seed questions otherwise
 */
export function useCountSeedQuestionsFetch(moduleId?: string, topics?: string[], statuses?: SeedQuestionStatus[]) {
  const {countSeedQuestions} = useCountSeedQuestions();
  const [count, setCount] = useState<number | undefined>(undefined);

  // Serialize arrays to avoid reference equality issues
  const topicsKey = topics?.join(',');
  const statusesKey = statuses?.join(',');

  useEffect(() => {
    // Create an abort controller to cancel previous requests
    let isCancelled = false;

    if (moduleId) {
      countSeedQuestions({moduleId, topics, statuses})
        .then((result) => {
          // Only update state if the request wasn't cancelled
          if (!isCancelled) {
            setCount(result);
          }
        })
        .catch((error) => {
          // Ignore AbortError - it's expected when requests are cancelled
          if (error.name !== 'AbortError' && !isCancelled) {
            console.error('Error fetching seed question count:', error);
          }
        });
    } else {
      setCount(undefined);
    }

    // Cleanup function to mark request as cancelled
    return () => {
      isCancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduleId, topicsKey, statusesKey]);

  return count;
}
